/**
 * Fast, deterministic field-level checks for inline display while editing.
 * Authoritative scrubber rules still run after auto-save and at sign.
 */
import { buildEncounterScrubberInput } from '@/lib/encounterScrubber/buildEncounterScrubberInput';
import type { FamilyTherapyParticipantAttendance } from '@/lib/encounterScrubber/buildEncounterScrubberInput';
import { getExpectedPsychotherapyCodeByDuration } from '@/lib/encounterScrubber/helpers';
import { scrubEncounterCptCodes } from '@/lib/encounterScrubber/encounterScrubber';
import type { EncounterScrubberResult } from '@/lib/encounterScrubber/types';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import type { AppointmentSessionType } from '@/pages/calendar/appointmentTypeCatalog';
import { enrichBillingCodesWithCpt } from '../noteMetadataMapping';

const PSYCHOTHERAPY_CODES = new Set(['90832', '90834', '90837']);
const DURATION_CPT_CODES = new Set([
  '90832',
  '90834',
  '90837',
  '90833',
  '90791',
  '90792',
  '90839',
  '90840',
  '90846',
  '90847',
  '90853',
]);

function selectedCptCodes(metadata: NoteMetadataSnapshot): string[] {
  return (metadata.billingCodes ?? [])
    .map((row) => row.code?.trim().toUpperCase())
    .filter((code): code is string => Boolean(code) && code !== 'UNSPECIFIED');
}

function billingRowIndexForCode(metadata: NoteMetadataSnapshot, code: string): number {
  return (metadata.billingCodes ?? []).findIndex(
    (row) => row.code?.trim().toUpperCase() === code.toUpperCase(),
  );
}

function applyScrubberInlineHints(
  errors: Record<string, string>,
  metadata: NoteMetadataSnapshot,
  results: EncounterScrubberResult[],
): void {
  const duration = metadata.durationMinutes;

  for (const result of results) {
    if (result.action === 'keep') continue;

    const rowIndex = billingRowIndexForCode(metadata, result.code);
    const codeUpper = result.code.toUpperCase();

    if (
      result.action === 'suggest_replace' &&
      result.suggestedCode &&
      DURATION_CPT_CODES.has(codeUpper)
    ) {
      if (duration != null && Number.isFinite(duration)) {
        errors.durationMinutes = `${duration} min usually maps to ${result.suggestedCode}, not ${result.code}.`;
      } else {
        errors.durationMinutes = 'Selected CPT may not match session duration.';
      }
      if (rowIndex >= 0) {
        errors[`billingRow-${rowIndex}`] =
          `Suggested: replace ${result.code} with ${result.suggestedCode}.`;
      }
      continue;
    }

    if (
      (result.action === 'billing_review' || result.action === 'suggest_remove') &&
      DURATION_CPT_CODES.has(codeUpper)
    ) {
      if (!errors.durationMinutes) {
        errors.durationMinutes =
          duration != null && Number.isFinite(duration)
            ? result.message
            : 'Selected CPT may not match session duration.';
      }
      if (rowIndex >= 0 && !errors[`billingRow-${rowIndex}`]) {
        errors[`billingRow-${rowIndex}`] = result.message;
      }
      continue;
    }

    if (rowIndex >= 0 && result.severity !== 'info') {
      errors[`billingRow-${rowIndex}`] = errors[`billingRow-${rowIndex}`] ?? result.message;
    }

    if (!errors.billingCodes && result.severity !== 'info') {
      errors.billingCodes = 'Selected CPT may not match session duration or billing rules.';
    }
  }
}

/**
 * Returns field-key → message map for inline helper text (e.g. durationMinutes, billingRow-0).
 */
export function buildInlineMetadataFieldChecks(
  metadata: NoteMetadataSnapshot | null,
  serviceTypeOptions: AppointmentSessionType[],
  participantAttendance?: FamilyTherapyParticipantAttendance,
): Record<string, string> {
  if (!metadata) return {};

  const enriched =
    serviceTypeOptions.length > 0
      ? enrichBillingCodesWithCpt(metadata, serviceTypeOptions)
      : metadata;
  const codes = selectedCptCodes(enriched);
  const errors: Record<string, string> = {};

  const duration = enriched.durationMinutes;
  const expectedPsych = getExpectedPsychotherapyCodeByDuration(duration);
  for (const code of codes) {
    if (PSYCHOTHERAPY_CODES.has(code) && expectedPsych && code !== expectedPsych) {
      errors.durationMinutes =
        duration != null && Number.isFinite(duration)
          ? `${duration} min usually maps to ${expectedPsych}, not ${code}.`
          : 'Selected CPT may not match session duration.';
      const rowIndex = billingRowIndexForCode(enriched, code);
      if (rowIndex >= 0) {
        errors[`billingRow-${rowIndex}`] = `Suggested: replace ${code} with ${expectedPsych}.`;
      }
      break;
    }
  }

  if (
    codes.some((c) => DURATION_CPT_CODES.has(c)) &&
    (duration == null || !Number.isFinite(duration))
  ) {
    errors.durationMinutes =
      errors.durationMinutes ??
      'Session duration is required to evaluate selected CPT codes.';
  }

  const scrubberInput = buildEncounterScrubberInput({
    metadata: enriched,
    serviceTypes: serviceTypeOptions,
    participantAttendance,
  });
  if (
    scrubberInput.currentCptCodes.length > 0 &&
    scrubberInput.providerAllowedCptCodes.length === 0
  ) {
    errors.billingCodes = 'No billable CPT codes are configured for this provider.';
  } else if (scrubberInput.currentCptCodes.length > 0) {
    applyScrubberInlineHints(errors, enriched, scrubEncounterCptCodes(scrubberInput));
  }

  return errors;
}
